import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { X, Trash2, Check, Tag, Plus } from 'lucide-react';
import type { WebsiteName, TaskStatus, TaskPriority } from '@/types/website';
import { WEBSITE_LABELS, TASK_STATUS_LABELS, TASK_PRIORITY_LABELS } from '@/types/website';
import { useWebsitePageTags } from '@/hooks/useWebsitePageTags';
import { cn } from '@/lib/utils';

interface WebsiteTaskBulkToolbarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onBulkUpdate: (updates: { website?: WebsiteName; status?: TaskStatus; priority?: TaskPriority; page_tags?: string[] }) => void;
  onBulkDelete: () => void;
}

export function WebsiteTaskBulkToolbar({
  selectedCount,
  onClearSelection,
  onBulkUpdate,
  onBulkDelete,
}: WebsiteTaskBulkToolbarProps) {
  const [selectedWebsite, setSelectedWebsite] = useState<WebsiteName | ''>('');
  const [selectedStatus, setSelectedStatus] = useState<TaskStatus | ''>('');
  const [selectedPriority, setSelectedPriority] = useState<TaskPriority | ''>('');
  const [selectedPageTag, setSelectedPageTag] = useState<string>('');
  const [newPageTag, setNewPageTag] = useState('');
  const [pageTagPopoverOpen, setPageTagPopoverOpen] = useState(false);
  
  const { data: availablePageTags = [] } = useWebsitePageTags();

  const hasChanges = selectedWebsite || selectedStatus || selectedPriority || selectedPageTag;

  const handleApplyAll = () => {
    const updates: { website?: WebsiteName; status?: TaskStatus; priority?: TaskPriority; page_tags?: string[] } = {};
    
    if (selectedWebsite) updates.website = selectedWebsite;
    if (selectedStatus) updates.status = selectedStatus;
    if (selectedPriority) updates.priority = selectedPriority;
    if (selectedPageTag) updates.page_tags = [selectedPageTag];
    
    onBulkUpdate(updates);
    
    // Reset all selections
    setSelectedWebsite('');
    setSelectedStatus('');
    setSelectedPriority('');
    setSelectedPageTag('');
  };

  const handleAddNewPageTag = () => {
    const trimmed = newPageTag.trim();
    if (trimmed) {
      setSelectedPageTag(trimmed);
      setNewPageTag('');
      setPageTagPopoverOpen(false);
    }
  };

  if (selectedCount === 0) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-background border rounded-lg shadow-lg p-3 flex items-center gap-3">
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="text-sm">
          {selectedCount} geselecteerd
        </Badge>
        <Button variant="ghost" size="sm" onClick={onClearSelection}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="h-6 w-px bg-border" />

      <Select value={selectedWebsite} onValueChange={(v) => setSelectedWebsite(v as WebsiteName)}>
        <SelectTrigger className="w-[130px] h-8">
          <SelectValue placeholder="Website" />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(WEBSITE_LABELS).map(([value, label]) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={selectedStatus} onValueChange={(v) => setSelectedStatus(v as TaskStatus)}>
        <SelectTrigger className="w-[130px] h-8">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(TASK_STATUS_LABELS).map(([value, label]) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={selectedPriority} onValueChange={(v) => setSelectedPriority(v as TaskPriority)}>
        <SelectTrigger className="w-[130px] h-8">
          <SelectValue placeholder="Prioriteit" />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(TASK_PRIORITY_LABELS).map(([value, label]) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Popover open={pageTagPopoverOpen} onOpenChange={setPageTagPopoverOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1">
            <Tag className="h-3.5 w-3.5" />
            {selectedPageTag || 'Pagina'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-3" align="start">
          <div className="space-y-3">
            <p className="text-sm font-medium">Pagina tag</p>
            
            {availablePageTags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {availablePageTags.map((tag) => (
                  <Badge
                    key={tag}
                    variant={selectedPageTag === tag ? 'default' : 'outline'}
                    className={cn(
                      'cursor-pointer transition-colors',
                      selectedPageTag === tag && 'bg-primary'
                    )}
                    onClick={() => {
                      setSelectedPageTag(tag);
                      setPageTagPopoverOpen(false);
                    }}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <Input
                placeholder="Nieuwe pagina..."
                value={newPageTag}
                onChange={(e) => setNewPageTag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddNewPageTag()}
                className="h-8 text-sm"
              />
              <Button size="sm" className="h-8 px-2" onClick={handleAddNewPageTag} disabled={!newPageTag.trim()}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <div className="h-6 w-px bg-border" />

      <Button 
        size="sm" 
        onClick={handleApplyAll} 
        disabled={!hasChanges}
        className="gap-1"
      >
        <Check className="h-4 w-4" />
        Toepassen
      </Button>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button size="sm" variant="destructive">
            <Trash2 className="h-4 w-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Taken verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je {selectedCount} {selectedCount === 1 ? 'taak' : 'taken'} wilt verwijderen?
              Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={onBulkDelete}>Verwijderen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
